import React from 'react'
import { TrendingUp, Weight, DollarSign, Database, Loader2 } from 'lucide-react'
import { ThemePreset } from '../../theme/presets'

interface KpiData {
  faturamento: number
  volumeKg: number
  ticketMedio: number
  statusCarga: {
    ultimaAtualizacao: string | null
    totalLinhas: number
    carregando: boolean
  }
}

interface KpiCardsProps {
  data: KpiData | null
  loading: boolean
  palette: ThemePreset
}

const fmtMoney = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

const fmtNumber = (value: number, digits = 2): string =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value)

const fmtDateTime = (value: string | null): string => {
  if (!value) return '--'
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(value))
  } catch {
    return value
  }
}

const KpiCard: React.FC<{
  label: string
  value: string
  icon: React.ReactNode
  palette: ThemePreset
  loading?: boolean
  sub?: string
}> = ({ label, value, icon, palette, loading, sub }) => (
  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow p-5">
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
          {label}
        </p>
        {loading ? (
          <div className="flex items-center gap-2 mt-1">
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <p className="text-2xl font-bold text-slate-900 dark:text-white truncate">
              {value}
            </p>
            {sub && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1 truncate">
                {sub}
              </p>
            )}
          </>
        )}
      </div>
      <div
        className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: `${palette.primary}15` }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          className: 'w-5 h-5',
          style: { color: palette.primary },
        })}
      </div>
    </div>
  </div>
)

export const KpiCards: React.FC<KpiCardsProps> = ({ data, loading, palette }) => {
  const isLoading = loading || !data

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <KpiCard
        label="Faturamento"
        value={data ? fmtMoney(data.faturamento) : '--'}
        icon={<DollarSign />}
        palette={palette}
        loading={isLoading}
      />
      <KpiCard
        label="Volume (kg)"
        value={data ? fmtNumber(data.volumeKg, 2) : '--'}
        icon={<Weight />}
        palette={palette}
        loading={isLoading}
      />
      <KpiCard
        label="Ticket Médio"
        value={data ? fmtMoney(data.ticketMedio) : '--'}
        icon={<TrendingUp />}
        palette={palette}
        loading={isLoading}
        sub="Preço médio por kg"
      />
      <KpiCard
        label="Status da Carga"
        value={data?.statusCarga.totalLinhas ? fmtNumber(data.statusCarga.totalLinhas, 0) : '--'}
        icon={<Database />}
        palette={palette}
        loading={data?.statusCarga.carregando ?? isLoading}
        sub={data?.statusCarga.ultimaAtualizacao ? `Atualizado em ${fmtDateTime(data.statusCarga.ultimaAtualizacao)}` : 'Sem dados'}
      />
    </div>
  )
}
